import { Calendar, CalendarChangeParams } from 'primereact/calendar';
import { MultiSelect, MultiSelectChangeParams } from 'primereact/multiselect';
import { Dropdown, DropdownChangeParams } from 'primereact/dropdown';
import { useCookies } from 'react-cookie';

import styles from './Filters.module.scss';
import {
	IManualInputFiltersData,
	IManualInputFiltersOptions
} from '../../models/manualInputFilters';
import { Control, Controller, useWatch } from 'react-hook-form';

export interface IFiltersProps {
	filtersDataControl: Control<IManualInputFiltersData, any>;
	filtersOptionsControl: Control<IManualInputFiltersOptions, any>;
	runSearch: any;
}

const Filters = ({
	filtersDataControl,
	filtersOptionsControl,
	runSearch
}: IFiltersProps): JSX.Element => {
	const [cookies, setCookie, removeCookie] = useCookies([
		'manualInputYear',
		'manualInputQuarter',
		'manualInputDivision',
		'manualInputCountry',
		'manualInputBrand',
		'manualInputCategories',
		'manualInputCompetitors'
	]);

	const {
		divisionOptions,
		countryOptions,
		brandOptions,
		productCategoryOptions
	} = useWatch({ control: filtersOptionsControl });

	const selectedProductCategories = useWatch({
		control: filtersDataControl,
		name: 'selectedProductCategoriesFilter'
	});

	return (
		<div className={styles.container}>
			<Controller
				name={'yearFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<Calendar
						className={`${styles.filter} ${styles.calendarFilter}`}
						placeholder='Year'
						{...field}
						onChange={(e: CalendarChangeParams) => {
							field.onChange(e.value as Date);
							setCookie('manualInputYear', e.value);
						}}
						view='year'
						dateFormat='yy'
						panelClassName={styles.calendarFilterPanel}
						appendTo='self'
					/>
				)}
			/>

			<Controller
				name={'quarterFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<Dropdown
						className={`${styles.filter} ${styles.quarterFilter}`}
						placeholder='Quarter'
						filter
						{...field}
						options={[
							{ label: 'Q1', value: 1 },
							{ label: 'Q2', value: 2 },
							{ label: 'Q3', value: 3 },
							{ label: 'Q4', value: 4 }
						]}
						onChange={(e) => {
							field.onChange(e);
							setCookie('manualInputQuarter', e.value);
						}}
						panelClassName={styles.quarterFilterPanel}
						appendTo='self'
					/>
				)}
			/>

			<Controller
				name={'divisionFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<Dropdown
						className={`${styles.filter} ${styles.divisionFilter}`}
						placeholder='Division'
						{...field}
						options={divisionOptions}
						onChange={(e: DropdownChangeParams) => {
							field.onChange(e);
							setCookie('manualInputDivision', e.value);
							removeCookie('manualInputCategories');
						}}
					/>
				)}
			/>

			<Controller
				name={'countryFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<Dropdown
						className={`${styles.filter} ${styles.countryFilter}`}
						placeholder='Country'
						{...field}
						options={countryOptions}
						onChange={(e: DropdownChangeParams) => {
							field.onChange(e);
							setCookie('manualInputCountry', e.value);
							removeCookie('manualInputBrand');
							removeCookie('manualInputCompetitors');
						}}
					/>
				)}
			/>

			<Controller
				name={'brandFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<Dropdown
						className={`${styles.filter} ${styles.brandFilter}`}
						placeholder='Brand'
						{...field}
						options={brandOptions}
						itemTemplate={(brand) => brand.label}
						onChange={(e: DropdownChangeParams) => {
							field.onChange(e);
							setCookie('manualInputBrand', e.value);
						}}
						filter
					/>
				)}
			/>

			<Controller
				name={'selectedProductCategoriesFilter'}
				control={filtersDataControl}
				render={({ field, fieldState }) => (
					<MultiSelect
						id={field.name}
						filter
						{...field}
						className={`${styles.filter} ${styles.categoryFilter}`}
						placeholder='Product category'
						options={productCategoryOptions}
						onChange={(e) => {
							field.onChange(e);
							setCookie('manualInputCategories', e.target.value);
						}}
					/>
				)}
			/>

			<button
				disabled={
					selectedProductCategories?.length <= 0 ||
					brandOptions?.length === 0
				}
				className={`${styles.runFilter} btn secondary`}
				onClick={runSearch}
			>
				Run
			</button>
		</div>
	);
};

export default Filters;
